/**
 * Launch URLs for the EasyAI Studio (the App Builder / integration assistant
 * that lives at `/dashboard/playground` in the EasyAI app).
 *
 * There are two ways in, and the difference matters:
 *
 *   1. Direct link — `/dashboard/playground?platform=nutrition#nutrition_key=<LICENSE>`
 *      Carries the license, but only works if the browser already holds an
 *      EasyAI session.
 *   2. SSO handoff — `/api/v2/sso/passio?token=<HMAC>&redirect=<encoded studio path>`
 *      Always lands signed in, but cannot carry the license.
 *
 * The license may only travel in the fragment. Fragments are never transmitted
 * to the server, so the key stays out of nginx access logs, our app logs and
 * Referer headers; the Studio reads it once on mount and rewrites the URL to the
 * bare pathname so it does not linger in the address bar or history.
 *
 * A license must never be put on the SSO link. `redirect` is a query param on a
 * real server request, so a key there would be logged — and it would not arrive
 * anyway: the SSO bootstrap page ends with `window.location.replace(...)` to a
 * fully-specified URL, which drops any fragment, including one nested inside
 * `redirect`. Signed-out users get the wizard's license step instead.
 */

/** Preselects the Studio mode and skips the wizard's picker step. */
export type StudioPlatform = "nutrition" | "easyai" | "sdk";

export type StudioMode = "chat" | "app_builder";

export interface StudioTarget {
  platform?: StudioPlatform;
  /**
   * Optional. `platform=nutrition` already implies `app_builder`. `platform=sdk`
   * does not force a mode, because SDK knowledge loads in Chat as well as App
   * Builder, so it has to be asked for explicitly.
   */
  mode?: StudioMode;
  /** 1 forces the wizard open with the picker shown; 0 suppresses it. */
  wizard?: 0 | 1;
  /**
   * Only meaningful with `platform: "sdk"`. Preselects the SDK and seeds the
   * builder target languages. Accepts `ios` | `android` | `react_native` |
   * `flutter` and their documented aliases. An unrecognised value does not
   * error; it just leaves the SDK unselected so the wizard asks.
   */
  sdkPlatform?: string;
}

export const STUDIO_PLAYGROUND_PATH = "/dashboard/playground";

/**
 * The entry points we surface. Deliberately organised by what the customer is
 * building against, not by Studio mode: `mode` is an implementation detail of
 * the wizard, and exposing "chat" and "build" as separate top-level buttons
 * made the choice harder rather than easier.
 */
export type StudioIntent = "nutritionApi" | "mobileSdk" | "existingApp";

export const STUDIO_INTENTS: Record<StudioIntent, StudioTarget> = {
  // The REST API. `platform=nutrition` preselects NutritionAI and forces App
  // Builder, because NutritionAI is ignored in Chat mode — so unlike the SDK
  // entry this one cannot offer question-asking. If EasyAI adds a
  // nutrition-aware Chat mode, drop `mode` handling in here and the two entries
  // become symmetric.
  nutritionApi: { platform: "nutrition" },
  // The mobile SDKs. No `mode` on purpose: unlike nutrition, `platform=sdk`
  // does not force one, so the wizard opens with the SDK preselected and lets
  // the customer choose asking questions or scaffolding an integration. That is
  // what makes a single "SDK assistant" button possible.
  mobileSdk: { platform: "sdk" },
  // Straight into the playground, past the wizard, where saved apps live.
  existingApp: { platform: "nutrition", wizard: 0 },
};

/**
 * Whether to put the license on the clipboard before handing off.
 *
 * True everywhere, because the Studio's wizard has a license step on every
 * path. This was briefly false for `mobileSdk` on the grounds that nothing
 * server-side consumes the key in SDK mode (no native preview proxy, and
 * generated code ships a `YOUR_PASSIO_KEY` placeholder) — but the wizard still
 * asks for it, so skipping the copy just left customers hunting for the key
 * with nothing on their clipboard.
 *
 * Kept as a map rather than inlined so a future intent that genuinely has no
 * license step can opt out without touching the launch code.
 */
export const STUDIO_INTENT_NEEDS_LICENSE: Record<StudioIntent, boolean> = {
  nutritionApi: true,
  mobileSdk: true,
  existingApp: true,
};

/**
 * Whether the Studio can answer mobile SDK questions. It can, so this now only
 * governs retiring the embedded SDK chatbot.
 *
 * The embed answers for anyone who loads the page, while the Studio needs a
 * provisioned EasyAI customer, so the chatbot stays as the fallback. Delete the
 * embed, this flag, and the fallback branch in `openMobileSdkAssistant` once the
 * fallback is provably unused — that also retires the plaintext EasyAI API key
 * currently shipped in the bundle via the iframe src.
 */
export const STUDIO_SUPPORTS_SDK = true as boolean;

const buildStudioQuery = (target: StudioTarget): string => {
  const params = new URLSearchParams();
  if (target.platform) params.set("platform", target.platform);
  if (target.mode) params.set("mode", target.mode);
  if (target.wizard !== undefined) params.set("wizard", String(target.wizard));
  if (target.sdkPlatform) params.set("sdk_platform", target.sdkPlatform);
  const query = params.toString();
  return query ? `?${query}` : "";
};

/** Host-relative Studio URL, safe to hand to the SSO `redirect` param. */
export const buildStudioPath = (target: StudioTarget): string =>
  `${STUDIO_PLAYGROUND_PATH}${buildStudioQuery(target)}`;

const trimBase = (baseUrl: string): string =>
  String(baseUrl || "")
    .trim()
    .replace(/\/+$/, "");

/**
 * Absolute Studio URL with the license in the fragment. Requires the browser to
 * already hold an EasyAI session.
 */
export const buildStudioDirectUrl = (
  baseUrl: string,
  target: StudioTarget,
  license?: string | null
): string => {
  const url = `${trimBase(baseUrl)}${buildStudioPath(target)}`;
  const key = String(license ?? "").trim();
  return key ? `${url}#nutrition_key=${encodeURIComponent(key)}` : url;
};

/**
 * Adds a `redirect` back into the Studio onto an SSO URL returned by
 * `GET /accounts/products/unified/easyai/sso-url`.
 *
 * Appended as a raw string rather than through URLSearchParams so the signed
 * `token` is passed along byte for byte. Never carries the license.
 */
export const withStudioRedirect = (
  ssoUrl: string,
  target: StudioTarget
): string => {
  const [base] = String(ssoUrl || "").split("#");
  if (!base) return base;
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}redirect=${encodeURIComponent(
    buildStudioPath(target)
  )}`;
};
